import { Box, Collapse, Typography } from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_ERROR_MESSAGE } from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import {
  addEmail,
  changePrimaryEmail,
  removeEmail,
  verifyEmail,
} from 'src/services/gigya/gigya-service';
import { ValidationRule } from 'src/utils/validator';
import AddEmail from './AddEmail';
import { EMAIL_LIMIT } from './constants';
import EmailRow from './EmailRow';
import { useStylesForm as useStyles } from './styles';
import { EmailType } from './types';

const getAddEmailErrorMessage = (errMessage: string) => {
  let message = DEFAULT_ERROR_MESSAGE;
  if (errMessage && errMessage.trim() === 'Login identifier exists') {
    message = 'Email already exists.';
  }
  return message;
};

const mapEmails = (unverifiedEmails, verifiedEmails) => [
  ...unverifiedEmails.map((email) => ({
    email,
    hide: false,
    type: EmailType.Unverified,
  })),
  ...verifiedEmails.map((email) => ({
    email,
    hide: false,
    type: EmailType.Verified,
  })),
];

const ChangeEmailForm: React.FC = () => {
  const { user, update } = useAuth();

  const { addNotification } = useNotifications();
  const classes = useStyles();

  const [emails, setEmails] = useState(
    mapEmails(user.unverifiedEmails, user.verifiedEmails)
  );

  useEffect(() => {
    setEmails(mapEmails(user.unverifiedEmails, user.verifiedEmails));
  }, [user.unverifiedEmails, user.verifiedEmails]);
  const primaryEmail = user.email ?? '';
  const emailNotInList: ValidationRule = useCallback(
    (value) => ({
      isValid: ![primaryEmail, ...emails.map((el) => el.email)].find(
        (el) => el.toLowerCase() === value.toLowerCase()
      ),
      helperText: 'Email is already added',
    }),
    [emails, primaryEmail]
  );

  const disableAdd = useMemo(
    () => emails.length >= EMAIL_LIMIT,
    [emails.length]
  );

  const errorNotification = useCallback(
    (_) => {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    },
    [addNotification]
  );

  const handleNewEmail = useCallback(
    (email) => {
      addEmail(
        email,
        () => {
          setEmails((prev) => [
            ...prev,
            { email, type: EmailType.Unverified, hide: true },
          ]);
          addNotification({
            title: 'Email address was added.',
            variant: 'success',
          });
          update();
        },
        (err) =>
          addNotification({
            title: getAddEmailErrorMessage(err),
            variant: 'error',
          })
      );
    },
    [addNotification, update]
  );

  const handleRemove = useCallback(
    (email) => {
      removeEmail(
        email,
        () => {
          setEmails((prev) => {
            const index = prev.findIndex((el) => el.email === email);
            return [
              ...prev.slice(0, index),
              { ...prev[index], hide: true },
              ...prev.slice(index + 1),
            ];
          });
          addNotification({
            title: 'Email address was removed.',
            variant: 'success',
          });
          update();
        },
        errorNotification
      );
    },
    [addNotification, update, errorNotification]
  );

  const handleMakePrimary = useCallback(
    (email) => {
      changePrimaryEmail(
        email,
        () => {
          addNotification({
            title: 'Primary email was changed.',
            variant: 'success',
          });
          update();
        },
        errorNotification
      );
    },
    [addNotification, update, errorNotification]
  );

  const handleVerify = useCallback(
    (email) => {
      verifyEmail(
        email,
        () => {
          addNotification({
            title: `Verification was resent to ${email}`,
            variant: 'success',
          });
        },
        errorNotification
      );
    },
    [addNotification, errorNotification]
  );

  const verifiedEmails = useMemo(
    () => emails.filter((em) => em.type === EmailType.Verified),
    [emails]
  );
  const unverifiedEmails = useMemo(
    () => emails.filter((em) => em.type === EmailType.Unverified),
    [emails]
  );

  return (
    <>
      <Box className={classes.primaryTitle}>
        <Typography variant="h5">Main email address</Typography>
        <Typography variant="body1" className={classes.emailTitle}>
          This is the email address we use to identify you. Your emails are
          associated with your Cambridge account.
        </Typography>
        <EmailRow email={primaryEmail} type={EmailType.Primary} />
      </Box>
      <Box className={classes.otherTitle}>
        <Typography variant="h5">Other email addresses</Typography>
        <Typography className={classes.emailTitle}>
          You can add more email addresses here and use them to sign in to your
          Cambridge account.
        </Typography>
        {!!unverifiedEmails.length && (
          <Box className={classes.unverifiedBlock}>
            {unverifiedEmails.map((el, index) => (
              <Collapse key={index} appear in={!el.hide}>
                <EmailRow
                  email={el.email}
                  type={EmailType.Unverified}
                  remove={() => handleRemove(el.email)}
                  verify={() => handleVerify(el.email)}
                />
              </Collapse>
            ))}
          </Box>
        )}
        {!!verifiedEmails.length && (
          <Box className={classes.verifiedBlock}>
            {verifiedEmails.map((el, index) => (
              <Collapse key={index} appear in={!el.hide}>
                <EmailRow
                  email={el.email}
                  type={EmailType.Verified}
                  remove={() => handleRemove(el.email)}
                  makePrimary={() => handleMakePrimary(el.email)}
                />
              </Collapse>
            ))}
          </Box>
        )}
      </Box>
      <AddEmail
        disabled={disableAdd}
        onUpdate={handleNewEmail}
        validationRules={[emailNotInList]}
      />
    </>
  );
};

export default memo(ChangeEmailForm);
